import { z } from "zod";
import { ROLES } from "./roles.js";

export const HelloResponseSchema = z.object({
  ok: z.boolean(),
  authenticated: z.boolean(),
  uid: z.string().nullable(),
  message: z.string(),
});
export type HelloResponse = z.infer<typeof HelloResponseSchema>;

export const WATERMARK_POSITIONS = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
  "center",
] as const;
export type WatermarkPosition = (typeof WATERMARK_POSITIONS)[number];

export const WatermarkConfigSchema = z.object({
  enabled: z.boolean().default(true),
  text: z.string().max(200).default("{family}™"),
  position: z.enum(WATERMARK_POSITIONS).default("bottom-right"),
  opacity: z.number().min(0).max(1).default(0.75),
  fontSize: z.number().int().min(8).max(120).default(28),
});
export type WatermarkConfig = z.infer<typeof WatermarkConfigSchema>;

export const FAMILY_THEMES = [
  "classic",
  "editorial",
  "modern",
  "dark-gold",
  "forest",
  "ocean",
  "clay",
] as const;
export type FamilyTheme = (typeof FAMILY_THEMES)[number];

export const HOME_LAYOUTS = ["timeline", "landing", "both"] as const;
export type HomeLayout = (typeof HOME_LAYOUTS)[number];

/** How a sign-up relates to the family tree's branches. */
export const BRANCH_STATUSES = ["descendant", "married_in", "not_sure"] as const;
export type BranchStatus = (typeof BRANCH_STATUSES)[number];

export const MembershipSchema = z.object({
  familyId: z.string().uuid(),
  familySlug: z.string(),
  familyName: z.string(),
  role: z.enum(ROLES),
  theme: z.enum(FAMILY_THEMES).default("classic"),
  homeLayout: z.enum(HOME_LAYOUTS).default("timeline"),
  watermark: WatermarkConfigSchema,
  /** Beta feature keys the user is opted into for this family. */
  features: z.array(z.string()).default([]),
  /** Signed view URLs for the family's custom logos, if uploaded. Null
   *  means the web app should use the static logo bundled in /public. */
  logoLightUrl: z.string().url().nullable().default(null),
  logoDarkUrl: z.string().url().nullable().default(null),
  /** Featured cover photo for the landing page. */
  coverUrl: z.string().url().nullable().default(null),
  /** Approved site accounts in this family (soft-deleted users excluded).
   *  Shown in the home page stats bar; distinct from persons in the tree. */
  registeredCount: z.number().int().nonnegative().default(0),
  /** True while this member is inside the family's welcome window (see
   *  families.welcome_days). The home page shows the editorial landing
   *  section on top of the feed only while this is true. */
  showWelcome: z.boolean().default(false),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const MeResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    firebaseUid: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    displayName: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    bio: z.string().nullable(),
    /** LANGUAGES codes; null = not answered yet. */
    languages: z.array(z.string()).nullable().default(null),
    city: z.string().nullable(),
    country: z.string().nullable(),
    /** When the user picked a place from the typeahead, these are set
     *  and win over re-geocoding city+country. Soft-fallback design:
     *  null is fine, the World map uses the city cache instead. */
    placeDisplayName: z.string().nullable(),
    placeLat: z.number().nullable(),
    placeLng: z.number().nullable(),
    /** Resolved avatar URL — the user's uploaded avatar (if any) takes
     *  precedence over the legacy avatar_url field. Signed when sourced
     *  from GCS. Null means "use initials". */
    avatarUrl: z.string().url().nullable(),
    /** True when the user hasn't completed onboarding (no firstName yet).
     *  Drives the post-sign-in redirect to the profile page. */
    needsOnboarding: z.boolean(),
    /** The id of the person row in the family tree linked to this user
     *  (persons.user_id = users.id), if any. Null means the user has
     *  not yet been added to the tree. */
    linkedPersonId: z.string().uuid().nullable(),
    /** True when the linked person already has a cover photo. Used by
     *  the profile page to skip the "use this on the tree too?" prompt
     *  when the user has explicitly placed a different photo there. */
    linkedPersonHasCover: z.boolean(),
    /** Sign-up answers used by moderators to place the user in the tree. */
    branchPersonId: z.string().uuid().nullable(),
    branchStatus: z.enum(BRANCH_STATUSES).nullable(),
    motherName: z.string().nullable(),
    fatherName: z.string().nullable(),
    knownRelative: z.string().nullable(),
    /** Push preferences for new content (see users.notify_*). */
    notifyPosts: z.boolean(),
    notifyNews: z.boolean(),
    notifyEvents: z.boolean(),
  }),
  memberships: z.array(MembershipSchema),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Languages offered on the profile; live-reunion pairing matches on them. */
export const LANGUAGES = ["am", "ti", "en"] as const;
export type Language = (typeof LANGUAGES)[number];
export const LANGUAGE_LABELS: Record<Language, string> = {
  am: "Amharic · አማርኛ",
  ti: "Tigrinya · ትግርኛ",
  en: "English",
};

export const UpdateProfileRequestSchema = z.object({
  firstName: z.string().min(1).max(120),
  lastName: z.string().max(120).optional().nullable(),
  bio: z.string().max(2000).optional().nullable(),
  city: z.string().max(120).optional().nullable(),
  country: z.string().max(120).optional().nullable(),
  /** From the place-autocomplete typeahead. When all three are sent,
   *  city/country come from the picked place's address; lat/lng are
   *  stored to skip re-geocoding on the World map. Sending all three
   *  as null clears the picked place (typeahead → free text). */
  placeDisplayName: z.string().max(500).optional().nullable(),
  placeLat: z.number().min(-90).max(90).optional().nullable(),
  placeLng: z.number().min(-180).max(180).optional().nullable(),
  /** Tree hints for moderators (see users.branch_*). undefined = leave as is. */
  branchPersonId: z.string().uuid().optional().nullable(),
  branchStatus: z.enum(BRANCH_STATUSES).optional().nullable(),
  motherName: z.string().max(160).optional().nullable(),
  fatherName: z.string().max(160).optional().nullable(),
  knownRelative: z.string().max(200).optional().nullable(),
  notifyPosts: z.boolean().optional(),
  notifyNews: z.boolean().optional(),
  notifyEvents: z.boolean().optional(),
  /** undefined = leave as is; [] = clear. */
  languages: z.array(z.enum(LANGUAGES)).max(LANGUAGES.length).optional(),
});
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

/** Claim which person in the family tree the caller is; null releases it.
 *  Populates persons.user_id, which "how am I related to…" depends on. */
export const LinkSelfToPersonRequestSchema = z.object({
  personId: z.string().uuid().nullable(),
});
export type LinkSelfToPersonRequest = z.infer<
  typeof LinkSelfToPersonRequestSchema
>;

/** Admin repair of a member's tree link — same release-then-claim semantics
 *  as the self-serve claim, but targeting another member. `override` lets the
 *  admin take a person already claimed by a different member (that member is
 *  unlinked); without it the API answers 409. */
export const AdminLinkMemberToPersonRequestSchema = z.object({
  personId: z.string().uuid().nullable(),
  override: z.boolean().default(false),
});
export type AdminLinkMemberToPersonRequest = z.infer<
  typeof AdminLinkMemberToPersonRequestSchema
>;

/** Avatar upload finalize. The browser already PUT bytes to the signed URL
 *  returned by /me/avatar/upload-url; this commits the path on the user
 *  row and optionally pushes the same image as the linked person's
 *  family-tree cover photo. */
export const FinalizeAvatarRequestSchema = z.object({
  storagePath: z.string().min(1),
  /** When true, also set the linked person's coverMediaId in the tree.
   *  Ignored when the user has no linked person row. */
  alsoUseOnTree: z.boolean().default(false),
});
export type FinalizeAvatarRequest = z.infer<typeof FinalizeAvatarRequestSchema>;

/** Avatar upload-url request. Mirrors the family logo flow but is
 *  user-scoped, not family-scoped. Same 2 MB cap; PNG/JPEG/WebP only
 *  (we don't allow SVG for personal photos). */
const AVATAR_MIMES = ["image/png", "image/jpeg", "image/webp"] as const;

export const AvatarUploadUrlRequestSchema = z.object({
  mimeType: z.enum(AVATAR_MIMES),
  fileSize: z.number().int().positive().max(2 * 1024 * 1024),
});
export type AvatarUploadUrlRequest = z.infer<typeof AvatarUploadUrlRequestSchema>;

export const AvatarUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storagePath: z.string(),
  expiresAt: z.string(),
});
export type AvatarUploadUrlResponse = z.infer<typeof AvatarUploadUrlResponseSchema>;

export const FamilyMemberSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  role: z.enum(ROLES),
  joinedAt: z.string(),
  /** Profile fields, surfaced so admins can fill them in for members
   *  who haven't completed their own profile yet (esp. phone-only signups). */
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  bio: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  placeDisplayName: z.string().nullable(),
  placeLat: z.number().nullable(),
  placeLng: z.number().nullable(),
  /** Tree identity (persons.user_id): which person in the family tree this
   *  member is, so admins can see and repair wrong claims. */
  linkedPersonId: z.string().uuid().nullable(),
  linkedPersonName: z.string().nullable(),
  /** Who approved this membership (null for pre-tracking members). */
  addedByName: z.string().nullable(),
});
export type FamilyMember = z.infer<typeof FamilyMemberSchema>;

// Family settings

export const FamilySettingsSchema = z.object({
  watermark: WatermarkConfigSchema,
  theme: z.enum(FAMILY_THEMES).default("classic"),
  homeLayout: z.enum(HOME_LAYOUTS).default("timeline"),
  /** Days after onboarding that the welcome section stays on the home
   *  page (layout "both" only). 0 = never automatically. */
  welcomeDays: z.number().int().min(0).max(365).default(7),
  /** Whose children are the branches. Null = auto-detected founder. */
  branchRootPersonId: z.string().uuid().nullable().default(null),
  /** AI chat turns allowed per rolling 30 days. Null = no limit. */
  aiTurnLimit30d: z.number().int().min(0).max(1_000_000).nullable().default(null),
});
export type FamilySettings = z.infer<typeof FamilySettingsSchema>;

/** A starting point offered in the UI — roughly a handful of questions a day
 *  for a small group of moderators. Not a default: the stored value stays
 *  null, meaning no limit, until somebody chooses one. */
export const SUGGESTED_AI_TURN_LIMIT_30D = 300;

export const UpdateFamilySettingsRequestSchema = z.object({
  watermark: WatermarkConfigSchema.partial().optional(),
  theme: z.enum(FAMILY_THEMES).optional(),
  homeLayout: z.enum(HOME_LAYOUTS).optional(),
  welcomeDays: z.number().int().min(0).max(365).optional(),
  branchRootPersonId: z.string().uuid().optional().nullable(),
  coverMediaId: z.string().uuid().optional(),
  aiTurnLimit30d: z.number().int().min(0).max(1_000_000).optional().nullable(),
});
export type UpdateFamilySettingsRequest = z.infer<
  typeof UpdateFamilySettingsRequestSchema
>;

// Branches — the founder's children. Sign-up picker + moderator routing.

export const BranchSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  birthYear: z.number().int().nullable(),
});
export const BranchesResponseSchema = z.object({
  /** The person whose children are listed; null when the tree has no root. */
  rootPersonId: z.string().uuid().nullable(),
  founderName: z.string().nullable(),
  /** True when an admin saved this root, false when it was auto-detected.
   *  Defaults to false so a new client against an older API degrades to
   *  "we can't vouch for this" rather than claiming a root is saved. */
  rootIsExplicit: z.boolean().default(false),
  branches: z.array(BranchSchema),
});
export type BranchesResponse = z.infer<typeof BranchesResponseSchema>;

export const BranchModeratorsResponseSchema = z.object({
  assignments: z.array(
    z.object({ branchPersonId: z.string().uuid(), userId: z.string().uuid() }),
  ),
});
export type BranchModeratorsResponse = z.infer<typeof BranchModeratorsResponseSchema>;
export const UpdateBranchModeratorsRequestSchema = z.object({
  branchPersonId: z.string().uuid(),
  userIds: z.array(z.string().uuid()).max(50),
});
export type UpdateBranchModeratorsRequest = z.infer<
  typeof UpdateBranchModeratorsRequestSchema
>;

// Tree audit (admin): who is in the tree but not attached to it.

/**
 * Where a person stands in relation to the family — a human's call, not the
 * graph's. TREE_PLACEMENTS below is what the relationships imply; this is
 * what someone decided. They are separate on purpose: an in-law's father is
 * correctly recorded and permanently "outside" by the graph, and that is not
 * an error anybody should keep being asked to fix.
 */
export const PERSON_STANDINGS = [
  "family",
  "married_in",
  "related_by_marriage",
  "friend",
  "not_family",
] as const;
export type PersonStanding = (typeof PERSON_STANDINGS)[number];

/** The two standings that count as being in the family tree. */
export const IN_TREE_STANDINGS: readonly PersonStanding[] = [
  "family",
  "married_in",
];

export const SetPersonStandingRequestSchema = z.object({
  /** Null clears the standing and returns the person to the review queue. */
  standing: z.enum(PERSON_STANDINGS).nullable(),
});
export type SetPersonStandingRequest = z.infer<
  typeof SetPersonStandingRequestSchema
>;

/** Headline tree figures for the home page. Computed server-side so the
 *  welcome bar and the Tree audit cannot disagree about what "in the tree"
 *  means — which they did, by forty people. */
export const TreeStatsResponseSchema = z.object({
  /** People the relationships actually place in the tree. */
  inTree: z.number().int().nonnegative(),
  /** Every person row, including those never linked to anyone. */
  onRecord: z.number().int().nonnegative(),
  generations: z.number().int().nonnegative(),
  /** Children of the founder, the same definition Settings uses. */
  branches: z.number().int().nonnegative(),
  yearSpan: z.number().int().nonnegative(),
});
export type TreeStatsResponse = z.infer<typeof TreeStatsResponseSchema>;

export const TREE_PLACEMENTS = [
  /** Founder, their ancestors, and every descendant of those. */
  "blood",
  /** Married to someone with blood placement. */
  "married_in",
  /** Connected to the tree, but neither of the above — typically the
   *  relatives of a married-in spouse (an in-law's own parents). */
  "outside",
  /** Has relationships, but the whole cluster is cut off from the founder. */
  "detached",
  /** No relationships at all — "Add person" with the linking step skipped. */
  "unlinked",
] as const;
export type TreePlacement = (typeof TREE_PLACEMENTS)[number];

export const TreeAuditPersonSchema = z.object({
  id: z.string().uuid(),
  givenName: z.string(),
  familyName: z.string().nullable(),
  nickname: z.string().nullable(),
  birthDate: z.string().nullable(),
  deathDate: z.string().nullable(),
  gender: z.string().nullable(),
  placement: z.enum(TREE_PLACEMENTS),
  /** The human's call, null until someone makes one. */
  standing: z.enum(PERSON_STANDINGS).nullable(),
  standingSetByName: z.string().nullable(),
  relationshipCount: z.number().int().nonnegative(),
  mediaTagCount: z.number().int().nonnegative(),
  /** Set when this person is the tree identity of a signed-up member. */
  linkedUserId: z.string().uuid().nullable(),
  /** For "outside" people: the family member they hang off. */
  connectedVia: z
    .object({ id: z.string().uuid(), name: z.string() })
    .nullable(),
  createdAt: z.string(),
  /** Null for persons added before created_by existed, and for imports. */
  createdById: z.string().uuid().nullable(),
  createdByName: z.string().nullable(),
});
export type TreeAuditPerson = z.infer<typeof TreeAuditPersonSchema>;

export const TreeAuditResponseSchema = z.object({
  rootPersonId: z.string().uuid().nullable(),
  founderName: z.string().nullable(),
  /** False when the founder was guessed rather than set in Settings. The
   *  guess picks the parentless person with the most descendants, which
   *  flips to an in-law's ancestor on trees that reach above the founder —
   *  and then the whole classification inverts, so the page says so. */
  rootIsExplicit: z.boolean(),
  /** Every person on record, whatever their standing. */
  total: z.number().int().nonnegative(),
  /** People actually in the tree: the graph places them there (blood or
   *  married in) AND no standing takes them out. A label can remove someone
   *  but never add them, so marking an unlinked person "family" does not
   *  inflate this. Someone tagged in a photo and never linked is not in it. */
  inTree: z.number().int().nonnegative(),
  /** By effective standing — the explicit one, else what the graph implies. */
  counts: z.object({
    family: z.number().int().nonnegative(),
    marriedIn: z.number().int().nonnegative(),
    relatedByMarriage: z.number().int().nonnegative(),
    friend: z.number().int().nonnegative(),
    notFamily: z.number().int().nonnegative(),
    /** No explicit standing and not placed in the tree by the graph. */
    unreviewed: z.number().int().nonnegative(),

    // Deprecated aliases of placementCounts, kept so a client built before
    // standings existed still renders during a release. docs/DEPLOY.md
    // mandates DB -> API -> web, so every release has a window where the
    // new API is serving the old page; without these, four stat cards on
    // the Tree audit page go blank for the length of it. Optional so
    // dropping them later is not a schema break — remove once no deployed
    // web build reads them.
    /** @deprecated use placementCounts.blood */
    blood: z.number().int().nonnegative().optional(),
    /** @deprecated use placementCounts.outside */
    outside: z.number().int().nonnegative().optional(),
    /** @deprecated use placementCounts.detached */
    detached: z.number().int().nonnegative().optional(),
    /** @deprecated use placementCounts.unlinked */
    unlinked: z.number().int().nonnegative().optional(),
  }),
  /** What the relationship graph says, regardless of standing. Kept so the
   *  page can still explain WHY someone surfaced. */
  placementCounts: z.object({
    blood: z.number().int().nonnegative(),
    marriedIn: z.number().int().nonnegative(),
    outside: z.number().int().nonnegative(),
    detached: z.number().int().nonnegative(),
    unlinked: z.number().int().nonnegative(),
  }),
  /** The review queue: unreviewed people the graph does not place in the
   *  tree. Giving someone a standing takes them off this list. */
  flagged: z.array(TreeAuditPersonSchema),
  /** The newest persons whatever their placement, so growth is visible. */
  recent: z.array(TreeAuditPersonSchema),
});
export type TreeAuditResponse = z.infer<typeof TreeAuditResponseSchema>;

// Pending sign-ups (Members page), with the tree hints they gave.

export const PendingUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  createdAt: z.string(),
  /** False while the sign-up is half-finished: the user signed in but never
   *  saved the onboarding form, so there is no name and no tree hints to
   *  show. Phone sign-ups in this state are titled by their number. */
  onboarded: z.boolean(),
  branchStatus: z.enum(BRANCH_STATUSES).nullable(),
  branchPersonId: z.string().uuid().nullable(),
  /** Given name of the branch person, resolved server-side. */
  branchName: z.string().nullable(),
  motherName: z.string().nullable(),
  fatherName: z.string().nullable(),
  knownRelative: z.string().nullable(),
});
export type PendingUser = z.infer<typeof PendingUserSchema>;
export const PendingUsersResponseSchema = z.object({
  users: z.array(PendingUserSchema),
});
export type PendingUsersResponse = z.infer<typeof PendingUsersResponseSchema>;

// Denied sign-ups (Members page) — restorable back to pending.

export const DeniedUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  deniedAt: z.string(),
  deniedByName: z.string().nullable(),
});
export type DeniedUser = z.infer<typeof DeniedUserSchema>;
export const DeniedUsersResponseSchema = z.object({
  users: z.array(DeniedUserSchema),
});
export type DeniedUsersResponse = z.infer<typeof DeniedUsersResponseSchema>;

// How-to videos (private bucket, signed URLs)

export const HowToVideoSchema = z.object({
  language: z.string(),
  label: z.string(),
  form: z.enum(["desktop", "mobile"]),
  url: z.string().url(),
});
export const HowToVideosResponseSchema = z.object({
  videos: z.array(HowToVideoSchema),
});
export type HowToVideosResponse = z.infer<typeof HowToVideosResponseSchema>;

export const FamilyMembersResponseSchema = z.object({
  family: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
  }),
  members: z.array(FamilyMemberSchema),
});
export type FamilyMembersResponse = z.infer<typeof FamilyMembersResponseSchema>;

// Invites

export const CreateInviteRequestSchema = z.object({
  role: z.enum(ROLES),
  expiresInHours: z.number().int().min(1).max(720).default(168),
});
export type CreateInviteRequest = z.infer<typeof CreateInviteRequestSchema>;

export const InviteSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(ROLES),
  url: z.string().url(),
  expiresAt: z.string(),
  consumedAt: z.string().nullable(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const InvitesListResponseSchema = z.object({
  invites: z.array(InviteSchema.omit({ url: true })),
});
export type InvitesListResponse = z.infer<typeof InvitesListResponseSchema>;

export const InvitePreviewResponseSchema = z.object({
  familyName: z.string(),
  familySlug: z.string(),
  role: z.enum(ROLES),
  expiresAt: z.string(),
  consumed: z.boolean(),
  expired: z.boolean(),
});
export type InvitePreviewResponse = z.infer<typeof InvitePreviewResponseSchema>;

export const AcceptInviteResponseSchema = z.object({
  familyId: z.string().uuid(),
  familySlug: z.string(),
  role: z.enum(ROLES),
});
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;

// Media

export const MEDIA_KINDS = ["photo", "video"] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const UploadUrlRequestSchema = z.object({
  kind: z.enum(MEDIA_KINDS),
  mimeType: z.string().min(1),
  fileSize: z.number().int().positive().max(5 * 1024 * 1024 * 1024), // 5 GB cap
});
export type UploadUrlRequest = z.infer<typeof UploadUrlRequestSchema>;

export const UploadUrlResponseSchema = z.object({
  mediaId: z.string().uuid(),
  uploadUrl: z.string().url(),
  storagePath: z.string(),
  expiresAt: z.string(),
});
export type UploadUrlResponse = z.infer<typeof UploadUrlResponseSchema>;

export const FinalizeMediaRequestSchema = z.object({
  mimeType: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  takenAt: z.string().datetime().optional(),
  durationMs: z.number().int().nonnegative().optional(),
  // Set by the in-app scanner. Literal list rather than DATE_PRECISIONS,
  // which is declared further down this module.
  takenAtPrecision: z.enum(["day", "month", "year"]).optional(),
  source: z.enum(["scan"]).optional(),
  caption: z.string().trim().max(2000).optional(),
  originalMediaId: z.string().uuid().optional(),
});
export type FinalizeMediaRequest = z.infer<typeof FinalizeMediaRequestSchema>;

export const TaggedPersonSchema = z.object({
  id: z.string().uuid(),
  tagId: z.string().uuid(),
  givenName: z.string(),
  familyName: z.string().nullable(),
});
export type TaggedPerson = z.infer<typeof TaggedPersonSchema>;

export const MediaRefSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(MEDIA_KINDS),
  viewUrl: z.string().url(),
  thumbnailUrl: z.string().url().nullable().optional(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  taggedPersons: z.array(TaggedPersonSchema).default([]),
  /** Spoken stories recorded for this photo (see media_voice_notes). */
  voiceNoteCount: z.number().int().nonnegative().optional(),
});
export type MediaRef = z.infer<typeof MediaRefSchema>;

const unit = z.number().min(0).max(1);
export const CreateMediaTagRequestSchema = z.object({
  personId: z.string().uuid(),
  // Where the person is in the photo, as fractions of width/height.
  bbox: z.object({ x: unit, y: unit, w: unit, h: unit }).optional(),
});
export type CreateMediaTagRequest = z.infer<typeof CreateMediaTagRequestSchema>;

export const PersonMediaItemSchema = z.object({
  mediaId: z.string().uuid(),
  mediaKind: z.enum(MEDIA_KINDS),
  viewUrl: z.string().url(),
  thumbnailUrl: z.string().url().nullable().optional(),
  postId: z.string().uuid(),
  postTitle: z.string().nullable(),
  eventDate: z.string().nullable(),
});
export type PersonMediaItem = z.infer<typeof PersonMediaItemSchema>;

export const PersonMediaListResponseSchema = z.object({
  media: z.array(PersonMediaItemSchema),
});
export type PersonMediaListResponse = z.infer<
  typeof PersonMediaListResponseSchema
>;

// News

export const NewsItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  summary: z.string().nullable(),
  body: z.string().nullable(),
  category: z.string(),
  coverMediaId: z.string().uuid().nullable(),
  coverUrl: z.string().url().nullable(),
  publishedAt: z.string(),
  author: z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
  }),
});
export type NewsItem = z.infer<typeof NewsItemSchema>;

export const NewsListResponseSchema = z.object({
  items: z.array(NewsItemSchema),
  categories: z.array(z.string()),
});
export type NewsListResponse = z.infer<typeof NewsListResponseSchema>;

export const CreateNewsRequestSchema = z.object({
  title: z.string().min(1).max(200),
  summary: z.string().max(500).optional(),
  body: z.string().max(50_000).optional(),
  category: z.string().max(80).optional(),
  coverMediaId: z.string().uuid().nullable().optional(),
  publishedAt: z.string().datetime().optional(),
});
export type CreateNewsRequest = z.infer<typeof CreateNewsRequestSchema>;

export const UpdateNewsRequestSchema = CreateNewsRequestSchema.partial();
export type UpdateNewsRequest = z.infer<typeof UpdateNewsRequestSchema>;

// Events

export const RSVP_STATUSES = ["yes", "no", "maybe"] as const;
export type RsvpStatus = (typeof RSVP_STATUSES)[number];

export const EventFaqItemSchema = z.object({
  question: z.string().max(500),
  answer: z.string().max(5000),
});
export type EventFaqItem = z.infer<typeof EventFaqItemSchema>;

export const EVENT_TYPES = ["wedding", "funeral", "reunion", "birthday", "holiday", "custom"] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** Default album suggestions per event type. Admin can customize. */
export const EVENT_TYPE_ALBUMS: Record<EventType, string[]> = {
  wedding: ["Ceremony", "Reception", "Family Photos", "Getting Ready", "Speeches"],
  funeral: ["Service", "Memorial", "Family", "Memories"],
  reunion: ["Group Photos", "Activities", "Food & Drinks", "Memories"],
  birthday: ["Celebration", "Cake & Gifts", "Group Photos"],
  holiday: ["Celebration", "Family", "Food"],
  custom: [],
};

export const EventAlbumSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  position: z.number().int(),
  mediaCount: z.number().int().nonnegative().optional(),
});
export type EventAlbum = z.infer<typeof EventAlbumSchema>;

export const EventAlbumsResponseSchema = z.object({
  albums: z.array(EventAlbumSchema),
});
export type EventAlbumsResponse = z.infer<typeof EventAlbumsResponseSchema>;

export const CreateEventAlbumRequestSchema = z.object({
  title: z.string().min(1).max(200),
  position: z.number().int().optional(),
});
export type CreateEventAlbumRequest = z.infer<typeof CreateEventAlbumRequestSchema>;

export const EventMediaItemSchema = z.object({
  id: z.string().uuid(),
  mediaId: z.string().uuid(),
  albumId: z.string().uuid().nullable(),
  viewUrl: z.string().url(),
  thumbnailUrl: z.string().url().nullable().optional(),
  kind: z.enum(MEDIA_KINDS),
  status: z.string().default("ready"),
  caption: z.string().nullable().optional(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  uploadedBy: z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
  }),
  createdAt: z.string(),
});
export type EventMediaItem = z.infer<typeof EventMediaItemSchema>;

export const EventMediaResponseSchema = z.object({
  media: z.array(EventMediaItemSchema),
});
export type EventMediaResponse = z.infer<typeof EventMediaResponseSchema>;

/** One proposed date in an undated event's optional date poll. */
export const EventDateOptionSchema = z.object({
  id: z.string().uuid(),
  date: z.string(), // YYYY-MM-DD
  votes: z.number().int().nonnegative(),
  votedByMe: z.boolean(),
  /** Who voted for this date, by display name. */
  voters: z.array(z.string()),
});
export type EventDateOption = z.infer<typeof EventDateOptionSchema>;

export const EventSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  eventType: z.string().default("custom"),
  /** YYYY-MM-DD, or null when the date isn't set yet ("Date TBA"). */
  eventDate: z.string().nullable(),
  endDate: z.string().nullable(),
  location: z.string().nullable(),
  venue: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  contactEmail: z.string().nullable(),
  paymentUrl: z.string().nullable(),
  faq: z.array(EventFaqItemSchema),
  rsvpEnabled: z.boolean(),
  /** The event has a live video room at /events/:id/live. */
  liveEnabled: z.boolean().default(false),
  /** Who may join the live call; "groups" means only liveGroupIds' members
   *  (hosts can always join). */
  liveAudience: z.enum(["everyone", "groups"]).default("everyone"),
  liveGroupIds: z.array(z.string().uuid()).default([]),
  /** The caller is in the live call's audience (hosts can join regardless). */
  liveInvited: z.boolean().default(false),
  coverMediaId: z.string().uuid().nullable(),
  coverUrl: z.string().url().nullable(),
  createdAt: z.string(),
  myRsvp: z.enum(RSVP_STATUSES).nullable(),
  myGuestCount: z.number().int().min(1).max(50).default(1),
  rsvpCounts: z.object({
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    maybe: z.number().int().nonnegative(),
  }),
  /** Total head count of attendees (sum of guest_count for status="yes"). */
  attendingTotal: z.number().int().nonnegative().default(0),
  albumCount: z.number().int().nonnegative().optional(),
  mediaCount: z.number().int().nonnegative().optional(),
  /** Date poll options, soonest first. Empty when there is no poll — always
   *  the case once the event has a date. */
  dateOptions: z.array(EventDateOptionSchema).default([]),
});
export type Event = z.infer<typeof EventSchema>;

export const EventsListResponseSchema = z.object({
  events: z.array(EventSchema),
});
export type EventsListResponse = z.infer<typeof EventsListResponseSchema>;

const DateRegex = /^\d{4}-\d{2}-\d{2}$/;

export const CreateEventRequestSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(10_000).optional(),
  eventType: z.string().max(40).optional(),
  /** Omit or null for an event whose date isn't set yet. */
  eventDate: z.string().regex(DateRegex, "must be YYYY-MM-DD").nullable().optional(),
  endDate: z.string().regex(DateRegex).nullable().optional(),
  location: z.string().max(500).optional(),
  venue: z.string().max(500).optional(),
  city: z.string().max(200).optional(),
  country: z.string().max(200).optional(),
  contactEmail: z.string().email().optional(),
  paymentUrl: z.string().url().optional(),
  faq: z.array(EventFaqItemSchema).max(50).optional(),
  rsvpEnabled: z.boolean().optional(),
  liveEnabled: z.boolean().optional(),
  liveAudience: z.enum(["everyone", "groups"]).optional(),
  /** Replaces the list when given. */
  liveGroupIds: z.array(z.string().uuid()).max(50).optional(),
  coverMediaId: z.string().uuid().nullable().optional(),
  /** Dates to put to a vote. Only used while the event has no date; on
   *  update it replaces the list (votes on dates that stay are kept). */
  dateOptions: z.array(z.string().regex(DateRegex)).max(10).optional(),
});
export type CreateEventRequest = z.infer<typeof CreateEventRequestSchema>;

export const UpdateEventRequestSchema = CreateEventRequestSchema.partial();
export type UpdateEventRequest = z.infer<typeof UpdateEventRequestSchema>;

export const SetDateVoteRequestSchema = z.object({
  optionId: z.string().uuid(),
  /** true = "I can make this date", false = take the vote back. */
  vote: z.boolean(),
});
export type SetDateVoteRequest = z.infer<typeof SetDateVoteRequestSchema>;

export const SetRsvpRequestSchema = z.object({
  status: z.enum(RSVP_STATUSES).nullable(),
  /** How many people are attending under this RSVP (responder + guests).
   *  Only meaningful when status === "yes"; otherwise ignored. */
  guestCount: z.number().int().min(1).max(50).optional(),
});
export type SetRsvpRequest = z.infer<typeof SetRsvpRequestSchema>;

/** One row in the admin attendee list for an event. */
export const EventRsvpAttendeeSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(RSVP_STATUSES),
  guestCount: z.number().int().min(1).max(50),
  updatedAt: z.string(),
});
export type EventRsvpAttendee = z.infer<typeof EventRsvpAttendeeSchema>;

export const EventRsvpListResponseSchema = z.object({
  attendees: z.array(EventRsvpAttendeeSchema),
  totals: z.object({
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    maybe: z.number().int().nonnegative(),
    /** Sum of guest_count across status="yes" RSVPs. */
    attendingTotal: z.number().int().nonnegative(),
  }),
});
export type EventRsvpListResponse = z.infer<typeof EventRsvpListResponseSchema>;

// Sessions (admin)

export const SessionEntrySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  ip: z.string(),
  userAgent: z.string().nullable(),
  country: z.string().nullable(),
  region: z.string().nullable(),
  city: z.string().nullable(),
  firstSeen: z.string(),
  lastSeen: z.string(),
  requestCount: z.number().int().nonnegative(),
  active: z.boolean(),
});
export type SessionEntry = z.infer<typeof SessionEntrySchema>;

export const SessionsListResponseSchema = z.object({
  sessions: z.array(SessionEntrySchema),
});
export type SessionsListResponse = z.infer<typeof SessionsListResponseSchema>;

// Stats (admin)

export const AdminStatsResponseSchema = z.object({
  totals: z.object({
    members: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    denied: z.number().int().nonnegative(),
    posts: z.number().int().nonnegative(),
    photos: z.number().int().nonnegative(),
    videos: z.number().int().nonnegative(),
    comments: z.number().int().nonnegative(),
    reactions: z.number().int().nonnegative(),
    events: z.number().int().nonnegative(),
    upcomingEvents: z.number().int().nonnegative(),
    openIssues: z.number().int().nonnegative(),
  }),
  activity: z.object({
    /** Distinct members with a session seen in the last 24h / 7d / 30d. */
    activeToday: z.number().int().nonnegative(),
    activeThisWeek: z.number().int().nonnegative(),
    activeThisMonth: z.number().int().nonnegative(),
    /** Members who have never posted, uploaded, or commented. */
    quietMembers: z.array(
      z.object({
        userId: z.string().uuid(),
        name: z.string(),
      }),
    ),
  }),
  /** Parallel arrays: one entry per day, oldest first, last entry = today. */
  series: z.object({
    days: z.array(z.string()),
    signups: z.array(z.number().int().nonnegative()),
    uploads: z.array(z.number().int().nonnegative()),
    posts: z.array(z.number().int().nonnegative()),
  }),
  ai: z.object({
    tokens30d: z.number().int().nonnegative(),
    calls30d: z.number().int().nonnegative(),
    turns30d: z.number().int().nonnegative(),
  }),
});
export type AdminStatsResponse = z.infer<typeof AdminStatsResponseSchema>;

// Groups

export const GROUP_TYPES = ["manual", "branch"] as const;
export type GroupType = (typeof GROUP_TYPES)[number];

export const FamilyGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  type: z.enum(GROUP_TYPES),
  rootPersonId: z.string().uuid().nullable(),
  memberCount: z.number().int().nonnegative().optional(),
  createdAt: z.string(),
});
export type FamilyGroup = z.infer<typeof FamilyGroupSchema>;

export const GroupsListResponseSchema = z.object({
  groups: z.array(FamilyGroupSchema),
});
export type GroupsListResponse = z.infer<typeof GroupsListResponseSchema>;

export const CreateGroupRequestSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  type: z.enum(GROUP_TYPES).optional(),
  rootPersonId: z.string().uuid().optional(),
  memberIds: z.array(z.string().uuid()).optional(),
});
export type CreateGroupRequest = z.infer<typeof CreateGroupRequestSchema>;

export const GroupMemberSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
});
export type GroupMember = z.infer<typeof GroupMemberSchema>;

export const GroupMembersResponseSchema = z.object({
  members: z.array(GroupMemberSchema),
});
export type GroupMembersResponse = z.infer<typeof GroupMembersResponseSchema>;

// Posts

/** Precision of a post's "pictured" date. */
export const DATE_PRECISIONS = ["day", "month", "year"] as const;
export type DatePrecision = (typeof DATE_PRECISIONS)[number];

/** Accepts `YYYY`, `YYYY-MM`, or `YYYY-MM-DD` for the eventDate field. */
const EventDateRegex = /^\d{4}(?:-\d{2}(?:-\d{2})?)?$/;

/** Optional location a post is tagged with (picked from place-autocomplete). */
const PostPlaceFields = {
  placeName: z.string().max(500),
  placeLat: z.number(),
  placeLng: z.number(),
};

export const CreatePostRequestSchema = z.object({
  title: z.string().max(200).optional(),
  body: z.string().min(1).max(50_000),
  eventDate: z
    .string()
    .regex(EventDateRegex, "must be YYYY, YYYY-MM, or YYYY-MM-DD")
    .optional(),
  mediaIds: z.array(z.string().uuid()).max(20).optional(),
  visibility: z.enum(["everyone", "groups"]).optional(),
  groupIds: z.array(z.string().uuid()).optional(),
  placeName: PostPlaceFields.placeName.optional(),
  placeLat: PostPlaceFields.placeLat.optional(),
  placeLng: PostPlaceFields.placeLng.optional(),
});
export type CreatePostRequest = z.infer<typeof CreatePostRequestSchema>;

export const UpdatePostRequestSchema = z.object({
  title: z.string().max(200).nullable().optional(),
  body: z.string().min(1).max(50_000).optional(),
  eventDate: z
    .string()
    .regex(EventDateRegex, "must be YYYY, YYYY-MM, or YYYY-MM-DD")
    .nullable()
    .optional(),
  placeName: PostPlaceFields.placeName.nullable().optional(),
  placeLat: PostPlaceFields.placeLat.nullable().optional(),
  placeLng: PostPlaceFields.placeLng.nullable().optional(),
});
export type UpdatePostRequest = z.infer<typeof UpdatePostRequestSchema>;

export const REACTION_KINDS = ["heart"] as const;
export type ReactionKind = (typeof REACTION_KINDS)[number];

export const PostReactionSummarySchema = z.object({
  heart: z.number().int().nonnegative(),
});
export type PostReactionSummary = z.infer<typeof PostReactionSummarySchema>;

export const PostSchema = z.object({
  id: z.string().uuid(),
  title: z.string().nullable(),
  body: z.string(),
  eventDate: z.string().nullable(), // YYYY-MM-DD (storage canonical form)
  eventDatePrecision: z.enum(DATE_PRECISIONS).default("day"),
  createdAt: z.string(),
  author: z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable().optional(),
  }),
  media: z.array(MediaRefSchema),
  reactions: PostReactionSummarySchema.optional(),
  myReactions: z.array(z.enum(REACTION_KINDS)).optional(),
  commentCount: z.number().int().nonnegative().optional(),
  visibility: z.string().optional(),
  groupNames: z.array(z.string()).optional(),
  // Optional tagged location (shown as a pin on the World map).
  placeName: z.string().nullable().optional(),
  placeLat: z.number().nullable().optional(),
  placeLng: z.number().nullable().optional(),
});
export type Post = z.infer<typeof PostSchema>;

export const PostsListResponseSchema = z.object({
  posts: z.array(PostSchema),
  nextCursor: z.string().nullable().optional(),
  hasMore: z.boolean().optional(),
});
export type PostsListResponse = z.infer<typeof PostsListResponseSchema>;

// Family tree

export const RELATIONSHIP_KINDS = ["parent", "spouse", "sibling"] as const;
export type RelationshipKind = (typeof RELATIONSHIP_KINDS)[number];

const DateOrNull = z
  .string()
  .regex(EventDateRegex, "must be YYYY, YYYY-MM, or YYYY-MM-DD")
  .nullable()
  .optional();

export const PersonSchema = z.object({
  id: z.string().uuid(),
  givenName: z.string(),
  familyName: z.string().nullable(),
  nickname: z.string().nullable(),
  birthDate: z.string().nullable(), // YYYY or YYYY-MM-DD (display form)
  birthDatePrecision: z.enum(DATE_PRECISIONS).default("day"),
  deathDate: z.string().nullable(),
  deathDatePrecision: z.enum(DATE_PRECISIONS).default("day"),
  gender: z.string().nullable(),
  bio: z.string().nullable(),
  userId: z.string().uuid().nullable(),
  coverMediaId: z.string().uuid().nullable(),
  coverUrl: z.string().url().nullable(),
  /** Null = nobody has judged yet. Unreviewed people still render in the
   *  tree; only an explicit non-family standing removes them. */
  standing: z.enum(PERSON_STANDINGS).nullable().default(null),
});
export type Person = z.infer<typeof PersonSchema>;

/** Does this person belong in the tree view? Unreviewed (null) counts as yes,
 *  so nothing disappears before a human has looked at it. */
export function showsInTree(standing: PersonStanding | null): boolean {
  return standing === null || IN_TREE_STANDINGS.includes(standing);
}

/**
 * Display name including the nickname the person is actually known by:
 * `Mekdelawit "Nunu" Kidane`. Used everywhere a Person is shown or picked,
 * so a search hit on the nickname is recognisable in the result list.
 */
export function personDisplayName(p: {
  givenName: string;
  familyName?: string | null;
  nickname?: string | null;
}): string {
  const nick = p.nickname?.trim();
  return (
    [p.givenName, nick ? `“${nick}”` : null, p.familyName]
      .filter(Boolean)
      .join(" ") || "Unnamed"
  );
}

export const CreatePersonRequestSchema = z.object({
  givenName: z.string().min(1).max(120),
  familyName: z.string().max(120).optional(),
  nickname: z.string().max(120).nullable().optional(),
  birthDate: DateOrNull,
  deathDate: DateOrNull,
  gender: z.string().max(40).nullable().optional(),
  bio: z.string().max(5000).optional(),
  coverMediaId: z.string().uuid().nullable().optional(),
  /** Settable at creation so photo tagging can record "not family" in the
   *  same request that makes the person, instead of creating a tree member
   *  and correcting it afterwards. */
  standing: z.enum(PERSON_STANDINGS).nullable().optional(),
});
export type CreatePersonRequest = z.infer<typeof CreatePersonRequestSchema>;

/** Standing is deliberately NOT updatable here: PATCH /persons/:id lets any
 *  member edit the person linked to themselves, and deciding your own
 *  standing in the family is not a personal field. It has its own endpoint
 *  behind tree:edit. */
export const UpdatePersonRequestSchema = CreatePersonRequestSchema.omit({
  standing: true,
}).partial();
export type UpdatePersonRequest = z.infer<typeof UpdatePersonRequestSchema>;

export const PersonsListResponseSchema = z.object({
  persons: z.array(PersonSchema),
});
export type PersonsListResponse = z.infer<typeof PersonsListResponseSchema>;

export const RelationshipSchema = z.object({
  id: z.string().uuid(),
  fromPerson: z.string().uuid(),
  toPerson: z.string().uuid(),
  kind: z.enum(RELATIONSHIP_KINDS),
});
export type Relationship = z.infer<typeof RelationshipSchema>;

export const CreateRelationshipRequestSchema = z.object({
  fromPerson: z.string().uuid(),
  toPerson: z.string().uuid(),
  kind: z.enum(RELATIONSHIP_KINDS),
});
export type CreateRelationshipRequest = z.infer<
  typeof CreateRelationshipRequestSchema
>;

export const RelationshipsListResponseSchema = z.object({
  relationships: z.array(RelationshipSchema),
});
export type RelationshipsListResponse = z.infer<
  typeof RelationshipsListResponseSchema
>;

// Comments

export const CommentSchema = z.object({
  id: z.string().uuid(),
  body: z.string(),
  createdAt: z.string(),
  author: z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable().optional(),
  }),
});
export type Comment = z.infer<typeof CommentSchema>;

export const CommentsListResponseSchema = z.object({
  comments: z.array(CommentSchema),
});
export type CommentsListResponse = z.infer<typeof CommentsListResponseSchema>;

export const CreateCommentRequestSchema = z.object({
  body: z.string().min(1).max(5000),
});
export type CreateCommentRequest = z.infer<typeof CreateCommentRequestSchema>;

// Reactions

export const ToggleReactionRequestSchema = z.object({
  kind: z.enum(REACTION_KINDS),
});
export type ToggleReactionRequest = z.infer<typeof ToggleReactionRequestSchema>;

// External tags (freeform name tags on media, not linked to the family tree)

export const ExternalTagSchema = z.object({
  id: z.string().uuid(),
  mediaId: z.string().uuid(),
  name: z.string(),
});
export type ExternalTag = z.infer<typeof ExternalTagSchema>;

export const CreateExternalTagRequestSchema = z.object({
  name: z.string().min(1).max(200).transform((s) => s.trim()),
});
export type CreateExternalTagRequest = z.infer<typeof CreateExternalTagRequestSchema>;

export const RenameExternalTagRequestSchema = z.object({
  oldName: z.string().min(1).max(200),
  newName: z.string().min(1).max(200).transform((s) => s.trim()),
});
export type RenameExternalTagRequest = z.infer<typeof RenameExternalTagRequestSchema>;

export const ExternalTagNameSchema = z.object({
  name: z.string(),
  count: z.number().int().nonnegative(),
});
export type ExternalTagName = z.infer<typeof ExternalTagNameSchema>;

export const ExternalTagNamesResponseSchema = z.object({
  names: z.array(ExternalTagNameSchema),
});
export type ExternalTagNamesResponse = z.infer<typeof ExternalTagNamesResponseSchema>;

// Beta features admin

export const BetaFeatureUserSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  grantedAt: z.string(),
});
export type BetaFeatureUser = z.infer<typeof BetaFeatureUserSchema>;

export const BetaFeatureSummarySchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  enrolledCount: z.number().int().nonnegative(),
});
export type BetaFeatureSummary = z.infer<typeof BetaFeatureSummarySchema>;

export const BetaFeaturesListResponseSchema = z.object({
  features: z.array(BetaFeatureSummarySchema),
});
export type BetaFeaturesListResponse = z.infer<
  typeof BetaFeaturesListResponseSchema
>;

export const BetaFeatureEnrollmentResponseSchema = z.object({
  feature: z.object({
    key: z.string(),
    label: z.string(),
    description: z.string(),
  }),
  users: z.array(BetaFeatureUserSchema),
});
export type BetaFeatureEnrollmentResponse = z.infer<
  typeof BetaFeatureEnrollmentResponseSchema
>;

export const GrantBetaFeatureRequestSchema = z.object({
  userId: z.string().uuid(),
});
export type GrantBetaFeatureRequest = z.infer<
  typeof GrantBetaFeatureRequestSchema
>;

// AI chat

export const AiChatRoleSchema = z.enum(["user", "assistant"]);
export type AiChatRole = z.infer<typeof AiChatRoleSchema>;

export const AiChatMessageSchema = z.object({
  role: AiChatRoleSchema,
  content: z.string().min(1).max(4000),
});
export type AiChatMessage = z.infer<typeof AiChatMessageSchema>;

/** Max conversation turns sent to the model (the context window). The server
 *  trims incoming history to this, and the client sends at most this many. */
export const AI_CHAT_MAX_MESSAGES = 20;

/** Hard upper bound on the messages array the API will accept, as an abuse
 *  guard. The server windows down to AI_CHAT_MAX_MESSAGES before calling the
 *  model, so a long history is trimmed rather than rejected. */
export const AI_CHAT_REQUEST_LIMIT = 100;

export const AiChatRequestSchema = z.object({
  /** Full message history, oldest first. The server windows this to the most
   *  recent turns and appends its reply. */
  messages: z.array(AiChatMessageSchema).min(1).max(AI_CHAT_REQUEST_LIMIT),
});
export type AiChatRequest = z.infer<typeof AiChatRequestSchema>;

export const AiChatToolCallSchema = z.object({
  name: z.string(),
  args: z.record(z.unknown()),
});
export type AiChatToolCall = z.infer<typeof AiChatToolCallSchema>;

export const AiChatResponseSchema = z.object({
  reply: z.string(),
  /** Tool calls the model made, surfaced for debugging / transparency. */
  toolCalls: z.array(AiChatToolCallSchema).default([]),
});
export type AiChatResponse = z.infer<typeof AiChatResponseSchema>;

// Trash (admin-only)

export const TRASH_KINDS = ["post", "event", "news"] as const;
export type TrashKind = (typeof TRASH_KINDS)[number];

export const TrashItemSchema = z.object({
  kind: z.enum(TRASH_KINDS),
  id: z.string().uuid(),
  title: z.string().nullable(),
  /** Author/creator of the item, when the table tracks one. */
  authorName: z.string().nullable(),
  /** Who pressed delete. */
  deletedByName: z.string().nullable(),
  deletedAt: z.string(),
  createdAt: z.string(),
});
export type TrashItem = z.infer<typeof TrashItemSchema>;

export const TrashListResponseSchema = z.object({
  items: z.array(TrashItemSchema),
});
export type TrashListResponse = z.infer<typeof TrashListResponseSchema>;

// Family logo upload (admin)

export const LOGO_VARIANTS = ["light", "dark"] as const;
export type LogoVariant = (typeof LOGO_VARIANTS)[number];

const LOGO_MIME_TYPES = [
  "image/png",
  "image/svg+xml",
  "image/webp",
  "image/jpeg",
] as const;

export const LogoUploadUrlRequestSchema = z.object({
  mimeType: z.enum(LOGO_MIME_TYPES),
  /** Bytes. 2 MB cap matches what we re-encode comfortably with sharp and
   *  keeps the served PNG/WebP small enough to be cached aggressively. */
  fileSize: z.number().int().positive().max(2 * 1024 * 1024),
});
export type LogoUploadUrlRequest = z.infer<typeof LogoUploadUrlRequestSchema>;

export const LogoUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storagePath: z.string(),
  expiresAt: z.string(),
});
export type LogoUploadUrlResponse = z.infer<typeof LogoUploadUrlResponseSchema>;

// Family world map / locations

export const FamilyLocationMemberSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  city: z.string(),
  country: z.string(),
  lat: z.number(),
  lng: z.number(),
  avatarUrl: z.string().url().nullable(),
  /** Short profile bio, shown in the map detail card. Null when unset. */
  bio: z.string().nullable(),
  /** Linked family-tree person, when this user has one — enables the
   *  "View in family tree" link from the map card. Null if not in the tree. */
  personId: z.string().uuid().nullable(),
});
export type FamilyLocationMember = z.infer<typeof FamilyLocationMemberSchema>;

export const FamilyLocationsResponseSchema = z.object({
  members: z.array(FamilyLocationMemberSchema),
  /** True if at least one member's city couldn't be geocoded (network /
   *  rate-limit / not found). The endpoint silently drops those — this
   *  flag tells the UI to show a small "some locations not shown" hint. */
  hasMissing: z.boolean(),
});
export type FamilyLocationsResponse = z.infer<
  typeof FamilyLocationsResponseSchema
>;

/** A post tagged with a location, surfaced as a pin on the World map. */
export const PostLocationSchema = z.object({
  postId: z.string().uuid(),
  title: z.string().nullable(),
  placeName: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  thumbnailUrl: z.string().nullable(),
});
export type PostLocation = z.infer<typeof PostLocationSchema>;

export const PostLocationsResponseSchema = z.object({
  posts: z.array(PostLocationSchema),
});
export type PostLocationsResponse = z.infer<typeof PostLocationsResponseSchema>;

// Place autocomplete (Nominatim proxy)

export const PlaceSuggestionSchema = z.object({
  /** Stable id from the geocoder, used as a React key. */
  id: z.string(),
  displayName: z.string(),
  city: z.string().nullable(),
  /** State / region / county — only meaningful for some countries. */
  region: z.string().nullable(),
  country: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
});
export type PlaceSuggestion = z.infer<typeof PlaceSuggestionSchema>;

export const PlaceSearchResponseSchema = z.object({
  results: z.array(PlaceSuggestionSchema),
});
export type PlaceSearchResponse = z.infer<typeof PlaceSearchResponseSchema>;

// ── Standalone Albums ─────────────────────────────────────────────────────────

export const AlbumSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  eventId: z.string().uuid().nullable(),
  eventTitle: z.string().nullable().optional(),
  visibility: z.enum(["everyone", "groups"]).default("everyone"),
  groupNames: z.array(z.string()).optional(),
  coverUrl: z.string().url().nullable(),
  mediaCount: z.number().int().nonnegative(),
  createdBy: z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
  }),
  createdAt: z.string(),
});
export type Album = z.infer<typeof AlbumSchema>;

export const AlbumsListResponseSchema = z.object({
  albums: z.array(AlbumSchema),
});
export type AlbumsListResponse = z.infer<typeof AlbumsListResponseSchema>;

export const CreateAlbumRequestSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  eventId: z.string().uuid().optional(),
  visibility: z.enum(["everyone", "groups"]).optional(),
  groupIds: z.array(z.string().uuid()).optional(),
});
export type CreateAlbumRequest = z.infer<typeof CreateAlbumRequestSchema>;

export const AlbumDetailSchema = z.object({
  album: AlbumSchema,
  media: z.array(z.object({
    id: z.string().uuid(),
    mediaId: z.string().uuid(),
    viewUrl: z.string().url(),
    thumbnailUrl: z.string().url().nullable().optional(),
    kind: z.enum(MEDIA_KINDS),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    position: z.number().int(),
    caption: z.string().nullable().optional(),
    takenAt: z.string().nullable().optional(),
    takenAtPrecision: z.enum(DATE_PRECISIONS).nullable().optional(),
    voiceNoteCount: z.number().int().nonnegative().optional(),
  })),
});
export type AlbumDetail = z.infer<typeof AlbumDetailSchema>;

/** One "photos were added" moment for the home feed: everything added to one
 *  album, or uploaded to one event, on one (UTC) day, newest first. Built from
 *  album_media / event_media on read, so it never duplicates or drifts from
 *  the album or event itself. */
export const AlbumActivitySchema = z.object({
  /** Stable key: `<kind>:<targetId>:<YYYY-MM-DD>`. */
  id: z.string(),
  /** Where the photos went: a standalone album or an event's photos. */
  kind: z.enum(["album", "event"]),
  /** The album id or event id, per `kind`. */
  targetId: z.string().uuid(),
  /** Album or event title. */
  title: z.string(),
  /** When the newest photo in this batch was added (ISO). */
  addedAt: z.string(),
  photoCount: z.number().int().positive(),
  /** Who added the photos, most photos first. For albums this is the
   *  media uploader, since album_media doesn't record who added a photo. */
  uploaders: z.array(z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable().optional(),
  })),
  /** The batch's photos for the carousel, newest first, capped server-side. */
  media: z.array(z.object({
    id: z.string().uuid(),
    kind: z.enum(MEDIA_KINDS),
    viewUrl: z.string().url(),
    thumbnailUrl: z.string().url().nullable().optional(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
  })),
});
export type AlbumActivity = z.infer<typeof AlbumActivitySchema>;

export const AlbumActivityResponseSchema = z.object({
  items: z.array(AlbumActivitySchema),
  /** Pass back as ?before= for the next page; null when there are no more. */
  nextCursor: z.string().nullable(),
});
export type AlbumActivityResponse = z.infer<typeof AlbumActivityResponseSchema>;

// ── Issues (user-reported site problems) ─────────────────────────────────────

/** Categories a reported issue can be filed under. Stored as plain text in
 *  the DB so adding a category here needs no migration. Keys are stable;
 *  labels are what the UI shows. */
export const ISSUE_CATEGORIES = [
  { key: "login_access", label: "Login & access" },
  { key: "family_tree", label: "Family tree" },
  { key: "photos_albums", label: "Photos & albums" },
  { key: "events", label: "Events" },
  { key: "news", label: "News" },
  { key: "ai_chat", label: "AI chat" },
  { key: "world_map", label: "World map" },
  { key: "performance", label: "Performance" },
  { key: "improvement", label: "Improvement idea" },
  { key: "other", label: "Other" },
] as const;
export type IssueCategoryKey = (typeof ISSUE_CATEGORIES)[number]["key"];

const ISSUE_CATEGORY_KEYS = ISSUE_CATEGORIES.map((c) => c.key) as [
  IssueCategoryKey,
  ...IssueCategoryKey[],
];

export const ISSUE_STATUSES = ["open", "in_progress", "resolved", "closed"] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export const CreateIssueRequestSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  category: z.enum(ISSUE_CATEGORY_KEYS).default("other"),
});
export type CreateIssueRequest = z.infer<typeof CreateIssueRequestSchema>;

export const UpdateIssueRequestSchema = z.object({
  status: z.enum(ISSUE_STATUSES).optional(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(5000).nullable().optional(),
  category: z.enum(ISSUE_CATEGORY_KEYS).optional(),
});
export type UpdateIssueRequest = z.infer<typeof UpdateIssueRequestSchema>;

/** Screenshot upload for an issue. Mirrors the avatar flow: signed PUT URL
 *  to a dedicated GCS prefix, then a finalize call that verifies the bytes
 *  landed. 5 MB cap; images only. */
const ISSUE_ATTACHMENT_MIMES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;

export const IssueAttachmentUploadUrlRequestSchema = z.object({
  mimeType: z.enum(ISSUE_ATTACHMENT_MIMES),
  fileSize: z.number().int().positive().max(5 * 1024 * 1024),
});
export type IssueAttachmentUploadUrlRequest = z.infer<
  typeof IssueAttachmentUploadUrlRequestSchema
>;

export const IssueAttachmentUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storagePath: z.string(),
  expiresAt: z.string(),
});
export type IssueAttachmentUploadUrlResponse = z.infer<
  typeof IssueAttachmentUploadUrlResponseSchema
>;

export const FinalizeIssueAttachmentRequestSchema = z.object({
  storagePath: z.string().min(1),
});
export type FinalizeIssueAttachmentRequest = z.infer<
  typeof FinalizeIssueAttachmentRequestSchema
>;

// ── Voice notes on photos ─────────────────────────────────────────────────────

export const VOICE_NOTE_MIMES = ["audio/webm", "audio/mp4", "audio/ogg", "audio/mpeg"] as const;
export const VOICE_NOTE_MAX_BYTES = 10 * 1024 * 1024;
export const VOICE_NOTE_MAX_MS = 2 * 60 * 1000;

export const VoiceNoteUploadUrlRequestSchema = z.object({
  // Recorders report e.g. "audio/webm;codecs=opus"; only the base type matters.
  mimeType: z.string().transform((m) => m.split(";")[0]!.trim().toLowerCase()).pipe(z.enum(VOICE_NOTE_MIMES)),
  fileSize: z.number().int().positive().max(VOICE_NOTE_MAX_BYTES),
});
export type VoiceNoteUploadUrlRequest = z.input<typeof VoiceNoteUploadUrlRequestSchema>;

export const VoiceNoteUploadUrlResponseSchema = z.object({
  voiceNoteId: z.string().uuid(),
  uploadUrl: z.string().url(),
  mimeType: z.enum(VOICE_NOTE_MIMES),
  expiresAt: z.string(),
});
export type VoiceNoteUploadUrlResponse = z.infer<typeof VoiceNoteUploadUrlResponseSchema>;

export const CreateVoiceNoteRequestSchema = z.object({
  voiceNoteId: z.string().uuid(),
  mimeType: z.enum(VOICE_NOTE_MIMES),
  durationMs: z.number().int().nonnegative().max(VOICE_NOTE_MAX_MS + 5000).optional(),
});
export type CreateVoiceNoteRequest = z.infer<typeof CreateVoiceNoteRequestSchema>;

/** "empty" = transcribed, but nothing intelligible was said. */
export const VOICE_NOTE_TRANSCRIPT_STATUSES = ["pending", "done", "failed", "empty"] as const;
export type VoiceNoteTranscriptStatus = (typeof VOICE_NOTE_TRANSCRIPT_STATUSES)[number];

export const VoiceNoteSchema = z.object({
  id: z.string().uuid(),
  url: z.string().url(),
  mimeType: z.string(),
  durationMs: z.number().int().nullable(),
  recordedBy: z.object({ id: z.string().uuid(), displayName: z.string().nullable() }),
  createdAt: z.string(),
  /** Verbatim, in the spoken language's own script. */
  transcript: z.string().nullable(),
  transcriptLang: z.string().nullable(),
  transcriptStatus: z.enum(VOICE_NOTE_TRANSCRIPT_STATUSES).nullable(),
  /** On-demand English translation, kept beside the original. */
  transcriptEn: z.string().nullable(),
});
export type VoiceNote = z.infer<typeof VoiceNoteSchema>;

export const UpdateVoiceNoteTranscriptRequestSchema = z.object({
  transcript: z.string().max(20_000),
});
export type UpdateVoiceNoteTranscriptRequest = z.infer<typeof UpdateVoiceNoteTranscriptRequestSchema>;

export const VoiceNoteTranslationResponseSchema = z.object({
  transcriptEn: z.string(),
});
export type VoiceNoteTranslationResponse = z.infer<typeof VoiceNoteTranslationResponseSchema>;

export const VoiceNotesListResponseSchema = z.object({
  voiceNotes: z.array(VoiceNoteSchema),
});
export type VoiceNotesListResponse = z.infer<typeof VoiceNotesListResponseSchema>;

// ── Live reunion ──────────────────────────────────────────────────────────

export const LIVE_ROUND_MINUTES_DEFAULT = 6;

export const LivePersonSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  /** What they are to the viewer, e.g. "second cousin"; null if the tree
   *  can't tell (one of them isn't linked to a person in it). */
  relation: z.string().nullable().default(null),
});
export type LivePerson = z.infer<typeof LivePersonSchema>;

/** Where the caller should be right now. "main" is the whole group; "round"
 *  is a timed 1:1 (or trio) room. A member who joined after the round
 *  started has no pair and waits in the main room (room is the main room,
 *  partners is empty). */
export const LiveStateSchema = z.object({
  phase: z.enum(["main", "round"]),
  round: z.number().int().nullable(),
  endsAt: z.string().nullable(),
  /** Server clock, so countdowns don't depend on the phone's clock. */
  serverNow: z.string(),
  room: z.string(),
  partners: z.array(LivePersonSchema),
  /** Photos for the pair to talk about (signed URLs, short-lived). */
  promptMedia: z
    .array(z.object({ mediaId: z.string().uuid(), url: z.string().url() }))
    .default([]),
  canHost: z.boolean(),
});
export type LiveState = z.infer<typeof LiveStateSchema>;

export const LiveTokenResponseSchema = z.object({
  url: z.string(),
  token: z.string(),
  room: z.string(),
  state: LiveStateSchema,
});
export type LiveTokenResponse = z.infer<typeof LiveTokenResponseSchema>;

export const StartLiveRoundRequestSchema = z.object({
  minutes: z.number().min(1).max(30).default(LIVE_ROUND_MINUTES_DEFAULT),
});
export type StartLiveRoundRequest = z.infer<typeof StartLiveRoundRequestSchema>;

/** What the host is showing everyone in the main room. Lives in the
 *  LiveKit room's metadata (set by the API), so phones that join late see
 *  it straight away. For a video, playback is "positionSec at `at`" and,
 *  while playing, phones run forward from there on the server's clock. */
export const LiveStageSchema = z.object({
  mediaId: z.string().uuid(),
  kind: z.enum(MEDIA_KINDS),
  /** The host presenting; their camera stays on screen, small. */
  by: z.string().uuid(),
  playing: z.boolean(),
  positionSec: z.number().min(0),
  at: z.string(),
});
export type LiveStage = z.infer<typeof LiveStageSchema>;

export const LiveRoomMetadataSchema = z.object({
  stage: LiveStageSchema.nullable(),
});
export type LiveRoomMetadata = z.infer<typeof LiveRoomMetadataSchema>;

/** Show a photo or video from the event to everyone; null stops. */
export const LivePresentRequestSchema = z.object({
  mediaId: z.string().uuid().nullable(),
  playing: z.boolean().default(false),
  positionSec: z.number().min(0).default(0),
});
export type LivePresentRequest = z.infer<typeof LivePresentRequestSchema>;

/** Participant metadata from the token, so phones can tell who hosts. */
export const LiveParticipantMetadataSchema = z.object({
  host: z.boolean(),
});

/** Data message topic. The payload is only a nudge to re-fetch the state —
 *  the API stays the source of truth, so a missed message costs nothing. */
export const LIVE_PHASE_TOPIC = "phase";
